import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  styled,
  TextField,
} from '@mui/material';
import { FileEarmarkText } from '@styled-icons/bootstrap';
import { UploadItemContainer } from 'FeatureModules/DataImport/Components/Forms/UploadItem';
import { FileNameText } from 'Components/FileUpload/styles';
import { ThemeTypography } from 'DesignSystem';
import RiskLevelBadge from 'DesignSystem/DataDisplay/RiskLevelBadge/RiskLevelBadge';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import React, { FC } from 'react';
import {
  EQuestionType,
  IQuestionnaireCompanySection,
} from 'FeatureModules/Questionnaire/types/questionnaire.types';
import Dropzone from 'Components/FileUpload/Dropzone';
import EmptyState from 'Components/EmptyState';
import FlexBox from 'Components/FlexBox';

interface IQuestionnaireFormViewOnlyProps {
  sections: IQuestionnaireCompanySection[];
  defaultExpanded?: boolean;
}

const Section = styled(FlexBox)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2),
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: theme.spacing(2),
  borderRadius: 6,
  background: theme.custom.themeColors.grayScale[5],
}));

const Tag = styled(ThemeTypography)(({ theme }) => ({
  borderRadius: 20,
  padding: theme.spacing(1, 2),
  background: theme.custom.themeColors.grayScale[20],
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  background: theme.custom.themeColors.white,
  width: '100%',
  borderRadius: 6,
  padding: theme.spacing(2),
  boxShadow: 'none',
  margin: '0 !important',
  minHeight: 'unset !important',
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    padding: 0,
    minHeight: 'auto',
    '& .MuiAccordionSummary-content': {
      margin: 0,
      paddingRight: theme.spacing(4),
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      position: 'absolute',
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
    },
  },
  '& .MuiAccordionDetails-root': {
    padding: theme.spacing(2, 0, 0, 0),
  },
}));

const QuestionnaireFormViewOnly: FC<IQuestionnaireFormViewOnlyProps> = ({
  sections,
  defaultExpanded,
}) => {
  return (
    <FlexBox flexDirection="column" gap={2} width="100%">
      {sections.map((section, sectionIndex) => (
        <Section key={section.id}>
          <FlexBox gap={2}>
            <Tag variant="BODY_MEDIUM_BOLD" color="BLUE_100">
              {sectionIndex + 1} of {sections.length}
            </Tag>
            <ThemeTypography variant="TITLE_SMALL" color="GRAY_60">
              {section.title}
            </ThemeTypography>
          </FlexBox>
          {section.description && (
            <ThemeTypography
              variant="BODY_MEDIUM"
              dangerouslySetInnerHTML={{ __html: section.description }}
            />
          )}
          {section.questions.map(
            (
              {
                id,
                questionText,
                isRequired,
                options,
                answerText,
                selectedOptionIds,
                description,
                questionType,
                riskLevel,
                commentText,
                proofDocuments,
              },
              questionIndex
            ) => (
              <StyledAccordion key={id} defaultExpanded={defaultExpanded}>
                <AccordionSummary
                  expandIcon={<Icon name="chevron-down" size="small" color="gray-60" />}
                >
                  <FlexBox gap={1}>
                    <ThemeTypography variant="BODY_LARGER_BOLD">
                      {sectionIndex + 1}.{questionIndex + 1}. {questionText}
                      {isRequired ? ' *' : ''}
                    </ThemeTypography>

                    {riskLevel && <RiskLevelBadge riskLevel={riskLevel} />}
                  </FlexBox>
                </AccordionSummary>

                <AccordionDetails>
                  {description && (
                    <ThemeTypography
                      variant="BODY_MEDIUM"
                      mb={2}
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  )}
                  {/* Text fields */}
                  {(questionType === EQuestionType.SHORT_FREE_TEXT ||
                    questionType === EQuestionType.LONG_FREE_TEXT) && (
                    <TextField
                      defaultValue={answerText}
                      disabled
                      fullWidth
                      label="Enter text"
                      variant="outlined"
                      {...(questionType === EQuestionType.LONG_FREE_TEXT
                        ? { multiline: true, maxRows: 5, minRows: 3 }
                        : {})}
                    />
                  )}

                  {/* Select radio buttons */}
                  {(questionType === EQuestionType.SINGLE_SELECT ||
                    questionType === EQuestionType.MULTI_SELECT) && (
                    <RadioGroup defaultValue={selectedOptionIds}>
                      {options?.map(({ id: optionId, text: optionText }) => (
                        <FormControlLabel
                          disabled
                          key={optionId}
                          value={optionId}
                          control={<Radio />}
                          label={optionText}
                        />
                      ))}
                    </RadioGroup>
                  )}

                  {/* Select dropdowns */}
                  {(questionType === EQuestionType.SINGLE_SELECT_DROP_DOWN ||
                    questionType === EQuestionType.MULTI_SELECT_DROP_DOWN) && (
                    <Select
                      multiple={questionType === EQuestionType.MULTI_SELECT_DROP_DOWN}
                      value={
                        questionType === EQuestionType.SINGLE_SELECT_DROP_DOWN
                          ? selectedOptionIds?.[0]
                          : Array.isArray(selectedOptionIds)
                          ? selectedOptionIds
                          : []
                      }
                      label={
                        questionType === EQuestionType.SINGLE_SELECT_DROP_DOWN
                          ? 'Select an option'
                          : 'Select options'
                      }
                      fullWidth
                      disabled
                    >
                      {options?.map(({ id: optionId, text: optionText }) => (
                        <MenuItem key={optionId} value={optionId}>
                          {optionText}
                        </MenuItem>
                      ))}
                    </Select>
                  )}

                  {/* Document upload field */}
                  {questionType === EQuestionType.DOCUMENTS && (
                    <>
                      {(proofDocuments === undefined || proofDocuments === null) && (
                        <Dropzone
                          disabled
                          supportedFileTypes={['pdf', 'image']}
                          maxFiles={20}
                          label="Your partner will be able to upload up to 20 files here"
                          onDrop={() => undefined}
                        />
                      )}

                      {proofDocuments?.length === 0 && (
                        <Box position="relative" height={150}>
                          <EmptyState message="No documents were uploaded" />
                        </Box>
                      )}

                      {!!proofDocuments?.length &&
                        proofDocuments.length > 0 &&
                        proofDocuments?.map(({ id, title, url }) => (
                          <UploadItemContainer
                            key={id}
                            onClick={() => window.open(url, '_blank')}
                            sx={{ cursor: 'pointer' }}
                          >
                            <FileEarmarkText size={28} />
                            <FlexBox width="100%" justifyContent="space-between">
                              <FileNameText>{title}</FileNameText>
                              <Icon name="link" />
                            </FlexBox>
                          </UploadItemContainer>
                        ))}
                    </>
                  )}

                  {!!commentText && (
                    <Box mt={2}>
                      <TextField
                        disabled
                        fullWidth
                        multiline
                        maxRows={5}
                        minRows={3}
                        label="Additional comments"
                        defaultValue={commentText}
                      />
                    </Box>
                  )}
                </AccordionDetails>
              </StyledAccordion>
            )
          )}
        </Section>
      ))}
    </FlexBox>
  );
};

export default QuestionnaireFormViewOnly;
